import { useState, useEffect, useMemo, useCallback } from "react";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { CustomPairsModal } from "./components/CustomPairsModal";
import { PositionControlModal } from "./components/PositionControlModal";
import { RecommendationDetailsModal } from "./components/RecommendationDetailsModal";
import { ManualApprovalPanel } from "./components/ManualApprovalPanel";
import { AIDecisionBadgeWithExpanded } from "./components/AIDecisionBadge";
import { ExpandedAIAnalysisRow } from "./components/ExpandedAIAnalysisRow";
import {
  updateCustomPairs,
  updateDataTradingMode,
  controlPosition,
  getWebSocketUrl,
  type UpdateTradingModeRequest,
  type PositionControlRequest,
  type ExecutionControlRequest,
  getExecutedTrades,
  type TradesFilterRequest,
} from "@app/services/tradingSystemApi";
import {
  getPortfolioState,
  getPortfolioHeat,
  getQueueLength,
  type PortfolioState,
  type HeatStatus,
} from "@app/services/capitalManagementApi";
import {
  getQueueStatus,
  getActiveRecommendations,
  getReserveRecommendations,
  type QueueStatus,
} from "@app/services/recommendationQueueApi";
import { logger } from "@app/services/logger";
import { useTradingSystemPayload } from "@app/hooks/useTradingSystemPayload";
import { useWebSocket } from "@app/hooks/useWebSocket";
import { useAppConfig } from "@app/hooks/useAppConfig";
import { useAuth } from "@app/hooks/useAuth";
import { listUsers, type User as AdminUser } from "@app/services/adminUsersApi";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { fetchAdminNotifications, acknowledgeNotification, type AdminNotification } from "@app/services/adminNotificationsApi";
import { listUserNotifications, markNotificationAsRead } from "@app/services/notificationsApi";

import type {
  TradingSystemPayload,
  TradingMode,
  ActivePosition,
  TradeRecommendation,
  ExecutedTrade,
  TradeStatus,
  TradesFilterResponse,
  TradesReport,
} from "../../../../shared/types";
import "./tradingSystem.css";

type TradeTimeRangeOption = "1h" | "today" | "yesterday" | "3d" | "7d" | "30d" | "custom";

interface TradeFiltersState {
  timeRange: TradeTimeRangeOption;
  status: "all" | TradeStatus;
  symbol: string;
  userId: string;
  startDate: string;
  endDate: string;
  limit: number;
}

const TIME_RANGE_TO_HOURS: Record<Exclude<TradeTimeRangeOption, "custom">, number> = {
  "1h": 1,
  "today": 0,
  "yesterday": -1,
  "3d": 72,
  "7d": 168,
  "30d": 720,
};

const TIME_RANGE_LABELS: Record<TradeTimeRangeOption, string> = {
  "1h": "Last hour",
  "today": "Today",
  "yesterday": "Yesterday",
  "3d": "Last 3 days",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  custom: "Custom range",
};

const TRADE_LIMIT_OPTIONS = [25, 50, 100, 200];

const TRADE_STATUS_OPTIONS: Array<{ label: string; value: TradeFiltersState["status"] }> = [
  { label: "All outcomes", value: "all" },
  { label: "Closed", value: "closed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Partially closed", value: "partially-closed" },
  { label: "Stopped out", value: "stopped-out" },
  { label: "Target hit", value: "target-hit" },
];

const DEFAULT_TRADE_FILTERS: TradeFiltersState = {
  timeRange: "today",
  status: "all",
  symbol: "",
  userId: "",
  startDate: "",
  endDate: "",
  limit: 100,
};

/**
 * Remove duplicate recommendations by symbol, keeping the most recent one.
 * 
 * This is a shared helper function to avoid code duplication across multiple
 * components that need deduplication logic.
 * 
 * @param recommendations - Array of recommendations to deduplicate
 * @returns Deduplicated array of recommendations
 */
function deduplicateRecommendationsBySymbol(
  recommendations: TradeRecommendation[]
): TradeRecommendation[] {
  if (!recommendations || recommendations.length === 0) {
    return [];
  }

  const uniqueRecommendationsMap = new Map<string, TradeRecommendation>();
  
  for (const rec of recommendations) {
    const existing = uniqueRecommendationsMap.get(rec.symbol);
    if (!existing) {
      uniqueRecommendationsMap.set(rec.symbol, rec);
    } else {
      // If duplicate symbol exists, keep the one with more recent generated_at
      const existingTime = new Date(existing.generated_at).getTime();
      const newTime = new Date(rec.generated_at).getTime();
      if (newTime > existingTime) {
        uniqueRecommendationsMap.set(rec.symbol, rec);
      }
    }
  }
  
  return Array.from(uniqueRecommendationsMap.values());
}

export function TradingSystemTab(): JSX.Element {
  const queryClient = useQueryClient();
  const { data: payload, isLoading, isFetching, error: queryError, refetch } = useTradingSystemPayload();
  const [error, setError] = useState<string | null>(null);
  const [customPairsOpen, setCustomPairsOpen] = useState(false);
  const [positionControlOpen, setPositionControlOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<ActivePosition | null>(null);
  const [recommendationDetailsOpen, setRecommendationDetailsOpen] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<TradeRecommendation | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: "success" | "error" | "warning" | "info" } | null>(null);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: "asc" | "desc" } | null>(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [processingRecommendations, setProcessingRecommendations] = useState<Set<string>>(new Set());
  const [processingPositions, setProcessingPositions] = useState<Set<string>>(new Set());
  const [expandedAIPositions, setExpandedAIPositions] = useState<Set<string>>(new Set());
  const [warningsDropdownOpen, setWarningsDropdownOpen] = useState(false);
  const [warningsDropdownTimeout, setWarningsDropdownTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [acknowledgedNotifications, setAcknowledgedNotifications] = useState<Set<string>>(new Set());
  const [portfolioState, setPortfolioState] = useState<PortfolioState | null>(null);
  const [portfolioHeat, setPortfolioHeat] = useState<HeatStatus | null>(null);
  const [queueLength, setQueueLength] = useState<number>(0);
  const [isLoadingCapitalData, setIsLoadingCapitalData] = useState(false);
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [isLoadingQueueData, setIsLoadingQueueData] = useState(false);
  const [executedTrades, setExecutedTrades] = useState<ExecutedTrade[]>([]);
  const [isLoadingExecutedTrades, setIsLoadingExecutedTrades] = useState(false);
  const [executedTradesError, setExecutedTradesError] = useState<string | null>(null);
  const [tradeFilters, setTradeFilters] = useState<TradeFiltersState>({ ...DEFAULT_TRADE_FILTERS });
  const [pendingTradeFilters, setPendingTradeFilters] = useState<TradeFiltersState>({ ...DEFAULT_TRADE_FILTERS });
  const [tradeFiltersDirty, setTradeFiltersDirty] = useState(false);
  const [hasLoadedExecutedTrades, setHasLoadedExecutedTrades] = useState(false);
  const [tradesLastUpdatedAt, setTradesLastUpdatedAt] = useState<string | null>(null);
  const [executedTradesMeta, setExecutedTradesMeta] = useState({
    page: 1,
    pageSize: DEFAULT_TRADE_FILTERS.limit,
    total: 0,
    hasNext: false,
  });
  const [appliedTradeFilters, setAppliedTradeFilters] = useState<TradesFilterResponse["applied_filters"] | null>(null);
  const [tradesReport, setTradesReport] = useState<TradesReport | null>(null);
  // Live clock for the open-position "Time Left" countdown column.
  // Ticks every second so the remaining lifetime is shown as a real countdown.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const { data: appConfig } = useAppConfig();
  const { user: currentUser } = useAuth();
  const isAdmin = appConfig?.role === "admin";
  const {
    data: adminTradeUsers = [],
    isLoading: isLoadingAdminTradeUsers,
  } = useQuery({
    queryKey: ["adminUsers", "trade-filters"],
    queryFn: () => listUsers(),
    enabled: isAdmin === true,
    staleTime: 60_000,
  });
  const adminTradeUserOptions = useMemo(() => {
    const currentUserId = currentUser?.user_id;
    return adminTradeUsers
      .filter((candidate) => candidate.id !== currentUserId)
      .sort((left, right) => left.email.localeCompare(right.email));
  }, [adminTradeUsers, currentUser?.user_id]);
  const activeTradeUserLabel = useMemo(() => {
    if (!isAdmin) {
      return null;
    }

    const appliedUserId = appliedTradeFilters?.user_id ?? currentUser?.user_id ?? null;
    if (!appliedUserId || appliedUserId === currentUser?.user_id) {
      return currentUser?.email ? `My trades (${currentUser.email})` : "My trades";
    }

    const matchedUser = adminTradeUsers.find((candidate) => candidate.id === appliedUserId);
    if (!matchedUser) {
      return appliedUserId;
    }

    return matchedUser.name
      ? `${matchedUser.email} (${matchedUser.name})`
      : matchedUser.email;
  }, [adminTradeUsers, appliedTradeFilters?.user_id, currentUser?.email, currentUser?.user_id, isAdmin]);
  const slotMetrics = useMemo(() => {
    if (!portfolioState) {
      return null;
    }
    const snapshot = portfolioState.slot_allocation;
    const seatsTotal = snapshot?.seats_total ?? Math.max(portfolioState.max_positions, 1);
    const seatsInUse = snapshot?.seats_in_use ?? Math.min(portfolioState.open_positions_count, seatsTotal);
    const seatsAvailable = snapshot?.seats_available ?? Math.max(seatsTotal - seatsInUse, 0);
    const perSlot =
      snapshot?.per_slot_capital ?? (seatsTotal > 0 ? portfolioState.total_equity / seatsTotal : 0);
    const seatShare =
      snapshot?.seat_share_for_request ??
      (seatsAvailable > 0 ? Math.min(perSlot, portfolioState.available_capital / seatsAvailable) : 0);
    let message: string;
    if (seatsAvailable === 0) {
      message = "All slots are in use. Approvals pause until a position exits.";
    } else if (portfolioState.available_capital <= 0) {
      message = "Available capital is 0 USDT. Approvals will fail until funds return.";
    } else if (seatShare + 1e-6 < perSlot) {
      // Check if the difference is due to 0.5% buffer (approximately 0.5% of perSlot)
      const bufferAmount = perSlot * 0.005;
      const actualDifference = perSlot - seatShare;
      if (Math.abs(actualDifference - bufferAmount) < 0.01) {
        // Difference is due to 0.5% buffer
        message = `Next slot will use $${seatShare.toFixed(2)} (99.5% of $${perSlot.toFixed(2)}) with 0.5% buffer reserved for slippage/fees.`;
      } else {
        // Difference is due to insufficient capital
        message = `Slot budget reduced to $${seatShare.toFixed(2)} because free capital is below the target $${perSlot.toFixed(
          2,
        )}.`;
      }
    } else {
      message = `Each slot currently targets $${perSlot.toFixed(2)} of capital.`;
    }
    return {
      perSlot,
      seatShare,
      seatsAvailable,
      seatsInUse,
      seatsTotal,
      message,
    };
  }, [portfolioState]);

  const updatePendingTradeFilters = useCallback((partial: Partial<TradeFiltersState>) => {
    setPendingTradeFilters((prev) => ({ ...prev, ...partial }));
    setTradeFiltersDirty(true);
  }, []);

  const buildTradesFilterRequest = useCallback(
    (filters: TradeFiltersState, targetPage: number): TradesFilterRequest => {
      const request: TradesFilterRequest = {
        page: targetPage,
        pageSize: filters.limit,
      };
      if (isAdmin) {
        if (filters.userId) {
          request.userId = filters.userId;
        } else {
          request.userId = currentUser?.user_id || undefined;
        }
      }
      if (filters.status !== "all") {
        request.status = filters.status;
      }
      if (filters.symbol.trim()) {
        request.symbol = filters.symbol.trim().toUpperCase();
      }
      if (filters.timeRange === "custom") {
        if (filters.startDate) {
          request.startDate = new Date(filters.startDate).toISOString();
        }
        if (filters.endDate) {
          request.endDate = new Date(filters.endDate).toISOString();
        }
      } else {
        // Send time_range parameter to backend for proper filtering
        // Backend will calculate the correct start_date based on time_range
        // This ensures accurate filtering without frontend timezone complications
        request.timeRange = filters.timeRange;
      }
      return request;
    },
    [isAdmin]
  );

  const loadExecutedTrades = useCallback(
    async (filters: TradeFiltersState, targetPage = 1, syncPending = false): Promise<void> => {
      if (filters.timeRange === "custom" && !filters.startDate) {
        setExecutedTradesError("Please select a start date for the custom time range.");
        return;
      }
      if (
        filters.timeRange === "custom" &&
        filters.startDate &&
        filters.endDate &&
        new Date(filters.startDate) > new Date(filters.endDate)
      ) {
        setExecutedTradesError("Start date must be earlier than end date.");
        return;
      }

      setIsLoadingExecutedTrades(true);
      setExecutedTradesError(null);
      try {
        const request = buildTradesFilterRequest(filters, targetPage);
        const response = await getExecutedTrades(request);
        setExecutedTrades(response.items);
        setExecutedTradesMeta({
          page: response.page,
          pageSize: response.page_size,
          total: response.total,
          hasNext: response.has_next,
        });
        setAppliedTradeFilters(response.applied_filters);
        setTradesReport(response.trades_report || null);
        setTradesLastUpdatedAt(new Date().toISOString());
        setHasLoadedExecutedTrades(true);
        setTradeFilters(filters);
        if (syncPending) {
          setPendingTradeFilters(filters);
          setTradeFiltersDirty(false);
        }
      } catch (err) {
        setExecutedTradesError(err instanceof Error ? err.message : "Failed to load executed trades.");
      } finally {
        setIsLoadingExecutedTrades(false);
      }
    },
    [buildTradesFilterRequest]
  );

  const handleWebSocketMessage = useCallback(
    (message: any) => {
      if (message.type === "update" && message.channel) {
        const channel = message.channel.replace("trading_system:updates:", "");
        const data = message.data || {};
        
        // Handle position closed event - refresh payload and executed trades immediately
        if (channel === "positions" && data.action === "position_closed") {
          const closedPositionId = data.position_id;
          const tradeLogId = data.trade_log_id;
          logger.info(`Position closed via WebSocket: ${data.symbol} (${closedPositionId})${tradeLogId ? `, trade_log_id: ${tradeLogId}` : ""}`);
          
          // CRITICAL FIX: Clean up expandedAIPositions state to prevent "Position not found" rows
          // Remove closed position from expanded state immediately
          setExpandedAIPositions((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(closedPositionId)) {
              newSet.delete(closedPositionId);
            }
            return newSet;
          });
          
          // CRITICAL FIX: Remove ai-decisions query for closed position to prevent 410 errors
          // This stops the hook from continuing to fetch for a closed position
          queryClient.removeQueries({
            queryKey: ["ai-decisions", closedPositionId],
          });
          
          // CRITICAL FIX: Use invalidateQueries instead of refetchQueries for immediate update
          // invalidateQueries marks data as stale and refetches immediately, ensuring position
          // is removed from active_positions array right away, even if data is fresh
          // refetchQueries only works if data is stale, causing delay in UI update
          queryClient.invalidateQueries({ 
            queryKey: ["trading-system", "payload"],
          });
          
          // CRITICAL FIX: Retry mechanism to ensure closed trade appears in Executed Trades
          // This handles race conditions between Database commit and Frontend query
          // Retry up to 3 times with exponential backoff (200ms, 400ms, 800ms)
          if (hasLoadedExecutedTrades && tradeLogId) {
            const verifyTradeAppeared = async (attempt: number = 1): Promise<void> => {
              const maxAttempts = 2;
              const delayMs = 200 * Math.pow(2, attempt - 1);
              
              try {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
                
                // Search page 1 (most recent) to verify the trade was saved
                const request = buildTradesFilterRequest(tradeFilters, 1);
                const response = await getExecutedTrades(request);
                
                const tradeFound = response.items.some((trade) => trade.id === tradeLogId);
                
                if (tradeFound) {
                  logger.info(`Trade ${tradeLogId} appeared in Executed Trades after ${attempt} attempt(s)`);
                  // CRITICAL FIX: Do NOT replace current page data with page 1 data.
                  // Instead, quietly refresh the current page so user stays on their page.
                  void loadExecutedTrades(tradeFilters, executedTradesMeta.page, false);
                } else if (attempt < maxAttempts) {
                  logger.debug(`Trade ${tradeLogId} not found in Executed Trades page 1 (attempt ${attempt}/${maxAttempts}), retrying...`);
                  await verifyTradeAppeared(attempt + 1);
                } else {
                  logger.debug(`Trade ${tradeLogId} not found after ${maxAttempts} attempts. Trade may be on a different page or filtered.`);
                }
              } catch (err) {
                logger.error(`Error verifying trade appearance (attempt ${attempt}): ${err instanceof Error ? err.message : String(err)}`);
                if (attempt < maxAttempts) {
                  await verifyTradeAppeared(attempt + 1);
                } else {
                  logger.debug(`Failed to verify trade after ${maxAttempts} attempts. Falling back to standard reload.`);
                  void loadExecutedTrades(tradeFilters, executedTradesMeta.page, false);
                }
              }
            };
            
            void verifyTradeAppeared();
          } else if (hasLoadedExecutedTrades) {
            // Fallback: If trade_log_id is not available, use standard reload with delay
            setTimeout(() => {
              void loadExecutedTrades(tradeFilters, executedTradesMeta.page, false);
            }, 200);
          }
          
          return;
        }
        
        if (channel === "recommendations" || channel === "positions" || channel === "execution_status" || channel === "payload") {
          // SUSTAINABLE SOLUTION: Use refetchQueries instead of invalidateQueries
          // This prevents immediate refetch if data is fresh, avoiding flickering
          // refetchQueries only refetches if query is active and data is stale
          queryClient.refetchQueries({ 
            queryKey: ["trading-system", "payload"],
            type: 'active'  // Only refetch active queries
          });
        }
        
        // Handle warnings channel - refresh admin notifications immediately
        if (channel === "warnings") {
          // Refresh admin notifications when warnings are received via WebSocket
          // This ensures manual close notifications appear immediately in the UI
          void refetchAdminNotifications();
        }
      }
    },
    [queryClient, hasLoadedExecutedTrades, tradeFilters, executedTradesMeta.page, loadExecutedTrades]
  );

  const wsUrl = useMemo(() => getWebSocketUrl(), []);
  
  // Fetch AdminNotifications (only for admin users)
  const { data: adminNotificationsData, refetch: refetchAdminNotifications } = useQuery({
    queryKey: ["admin-notifications"],
    queryFn: () => fetchAdminNotifications(50),
    enabled: isAdmin === true, // Only fetch if user is admin
    staleTime: 30_000, // 30 seconds
    refetchInterval: (query) => {
      // Don't refetch if user is not admin
      if (!isAdmin) {
        return false;
      }
      
      // SUSTAINABLE SOLUTION: Stop refetching on 404 errors (endpoint not found)
      // This prevents continuous error spam in console when endpoint is unavailable
      const error = query.state.error;
      if (error) {
        const errorMessage = error.message;
        const statusCode = (error as Error & { statusCode?: number })?.statusCode;
        
        // Stop refetching for 404 errors (endpoint not found)
        if (
          statusCode === 404 ||
          errorMessage.includes("404") ||
          errorMessage.includes("Not Found")
        ) {
          return false;
        }
      }
      return 60_000; // Refetch every minute if no error
    },
    retry: (failureCount, error) => {
      // Don't retry if user is not admin
      if (!isAdmin) {
        return false;
      }
      
      // SUSTAINABLE SOLUTION: Don't retry on 404 errors (endpoint not found)
      // This prevents unnecessary retry attempts when endpoint is unavailable
      const errorMessage = error.message;
      const statusCode = (error as Error & { statusCode?: number })?.statusCode;
      
      if (
        statusCode === 404 ||
        errorMessage.includes("404") ||
        errorMessage.includes("Not Found")
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
  
  // Acknowledge notification mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (notificationId: string) => acknowledgeNotification(notificationId),
    onSuccess: (data, notificationId) => {
      // CRITICAL FIX: Keep the notification in acknowledgedNotifications set
      // Don't clear it, so it stays filtered out even after refetch
      // Also filter out notifications that are acknowledged in the backend (acknowledgedBy is set)
      setAcknowledgedNotifications((prev) => new Set([...prev, notificationId]));
      
      // Delay refetch to allow backend to update the notification
      setTimeout(() => {
        void refetchAdminNotifications();
      }, 500);
    },
  });

  // Fetch user notifications for subscribers
  const { data: userNotificationsData, refetch: refetchUserNotifications } = useQuery({
    queryKey: ["user-notifications-trading"],
    queryFn: () => listUserNotifications(),
    enabled: isAdmin === false,
    staleTime: 30_000,
    refetchInterval: isAdmin ? false : 60_000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Mark-as-read mutation for user notifications
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => markNotificationAsRead(notificationId),
    onSuccess: () => {
      setTimeout(() => {
        void refetchUserNotifications();
      }, 500);
    },
  });

  // Trading mode change mutation (AUTO / MANUAL / DISABLED)
  const modeChangeMutation = useMutation({
    mutationFn: (mode: TradingMode) => updateDataTradingMode({ mode }),
    onSuccess: (_data, mode) => {
      void queryClient.invalidateQueries({ queryKey: ["trading-system", "payload"] });
      const labels: Record<TradingMode, string> = {
        auto: "Auto (automated execution)",
        manual: "Manual (await approval)",
        disabled: "Disabled",
      };
      showNotification(`Trading mode set to ${labels[mode]}`, "success");
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Failed to change trading mode";
      setError(message);
      showNotification(message, "error");
    },
  });

  // Track dismissed user notifications (client-side)
  const [dismissedUserNotifications, setDismissedUserNotifications] = useState<Set<string>>(new Set());

  // Active user notifications (filtered)
  const activeUserNotifications = useMemo(() => {
    if (!userNotificationsData) return [];
    return userNotificationsData.filter((notif) => {
      if (dismissedUserNotifications.has(notif.id)) return false;
      if (notif.read_at) return false;
      return true;
    });
  }, [userNotificationsData, dismissedUserNotifications]);

  // Filter out expired and acknowledged AdminNotifications
  const activeAdminNotifications = useMemo(() => {
    if (!adminNotificationsData?.notifications) return [];
    const now = new Date();
    return adminNotificationsData.notifications.filter((notif) => {
      // Filter out expired notifications
      if (notif.expiresAt) {
        const expiresAt = new Date(notif.expiresAt);
        if (expiresAt <= now) {
          return false;
        }
      }
      // Filter out acknowledged notifications (both client-side and backend-acknowledged)
      if (acknowledgedNotifications.has(notif.id) || notif.acknowledgedBy) {
        return false;
      }
      return true;
    });
  }, [adminNotificationsData, acknowledgedNotifications]);
  
  // Merge warnings with AdminNotifications + UserNotifications
  const allWarnings = useMemo(() => {
    const payloadWarnings = Array.isArray(payload?.warnings) ? payload.warnings : [];
    const adminWarnings = activeAdminNotifications.map((notif) => ({
      id: notif.id,
      type: notif.severity as "error" | "warning" | "info",
      message: `${notif.title}: ${notif.message}`,
      isAdminNotification: true,
      notification: notif,
    }));
    // User-level notifications for subscribers (in same format as admin)
    const userWarnings = activeUserNotifications.map((notif) => ({
      id: notif.id,
      type: notif.severity as "error" | "warning" | "info",
      message: `${notif.title}: ${notif.message}`,
      isAdminNotification: false,
      isUserNotification: true,
      notification: notif,
    }));
    const stringWarnings = payloadWarnings.map((w) => ({
      id: `warning-${w}`,
      type: w.includes("⚠️") || w.includes("CRITICAL") || w.includes("ERROR")
        ? ("error" as const)
        : w.includes("WARNING")
        ? ("warning" as const)
        : ("info" as const),
      message: w,
      isAdminNotification: false,
    }));
    return [...adminWarnings, ...userWarnings, ...stringWarnings];
  }, [payload?.warnings, activeAdminNotifications, activeUserNotifications]);

  useWebSocket({
    url: wsUrl,
    onMessage: handleWebSocketMessage,
    onError: (error) => {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error("WebSocket error", normalizedError);
    },
    onOpen: () => {
      logger.info("WebSocket connected for trading system updates");
    },
    onClose: () => {
      logger.info("WebSocket disconnected");
    },
    enabled: !isLoading && !isFetching && payload !== undefined,
    maxReconnectAttempts: 3,
  });

  useEffect(() => {
    loadCapitalManagementData();
    loadRecommendationQueueData();
  }, []);

  useEffect(() => {
    if (payload) {
      loadCapitalManagementData();
      loadRecommendationQueueData();
    }
  }, [payload]);

  // SUSTAINABLE SOLUTION: Rely on refetchInterval (30s) for stable updates
  // Removed aggressive stale checking that was causing recommendations to disappear
  // Backend queue service handles stale filtering (10 minutes threshold)
  // Frontend should display what backend provides - stable and consistent
  // No need for immediate refetch on stale detection - let refetchInterval handle updates

  const loadPayload = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    if (!hasLoadedExecutedTrades) {
      void loadExecutedTrades({ ...DEFAULT_TRADE_FILTERS }, 1, true);
    }
  }, [hasLoadedExecutedTrades, loadExecutedTrades]);

  // CRITICAL FIX: Clean up expandedAIPositions state when positions are removed
  // This ensures that if a position is closed (removed from active_positions),
  // it is also removed from expandedAIPositions state, preventing "Position not found" rows
  // This handles cases where WebSocket event might be missed or delayed
  useEffect(() => {
    if (!payload?.active_positions) {
      return;
    }

    const activePositionIds = new Set(payload.active_positions.map((pos) => pos.id));
    
    setExpandedAIPositions((prev) => {
      const cleaned = new Set<string>();
      for (const positionId of prev) {
        if (activePositionIds.has(positionId)) {
          cleaned.add(positionId);
        } else {
          // Position was closed - remove from expanded state and clean up its query
          queryClient.removeQueries({
            queryKey: ["ai-decisions", positionId],
          });
        }
      }
      return cleaned;
    });
  }, [payload?.active_positions, queryClient]);

  const handleApplyTradeFilters = useCallback(async (): Promise<void> => {
    await loadExecutedTrades(pendingTradeFilters, 1, true);
  }, [loadExecutedTrades, pendingTradeFilters]);

  const handleResetTradeFilters = useCallback(async (): Promise<void> => {
    await loadExecutedTrades({ ...DEFAULT_TRADE_FILTERS }, 1, true);
  }, [loadExecutedTrades]);

  const handleRefreshExecutedTrades = useCallback(async (): Promise<void> => {
    await loadExecutedTrades(tradeFilters, executedTradesMeta.page, false);
  }, [loadExecutedTrades, tradeFilters, executedTradesMeta.page]);

  const handleTradePageChange = useCallback(
    async (direction: "prev" | "next"): Promise<void> => {
      if (direction === "prev" && executedTradesMeta.page === 1) {
        return;
      }
      if (direction === "next" && !executedTradesMeta.hasNext) {
        return;
      }
      const nextPage =
        direction === "prev" ? Math.max(1, executedTradesMeta.page - 1) : executedTradesMeta.page + 1;
      await loadExecutedTrades(tradeFilters, nextPage, false);
    },
    [executedTradesMeta.hasNext, executedTradesMeta.page, loadExecutedTrades, tradeFilters]
  );

  const formatDateTime = useCallback((value?: string | null): string => {
    if (!value) {
      return "N/A";
    }
    try {
      // Parse UTC timestamp string (from backend) and convert to local timezone
      let date: Date;
      if (typeof value === "string") {
        // Ensure UTC timezone is properly indicated
        if (value.endsWith("Z") || value.includes("+") || value.includes("-", 10)) {
          date = new Date(value);
        } else {
          // If no timezone indicator, assume UTC and add Z
          date = new Date(value + "Z");
        }
      } else {
        date = new Date(value);
      }
      
      if (Number.isNaN(date.getTime())) {
        return "N/A";
      }
      
      // Use Intl.DateTimeFormat to properly convert UTC to local timezone
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formatter = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: userTimeZone,
      });
      
      // Format the date in local timezone
      const parts = formatter.formatToParts(date);
      const year = parts.find((p) => p.type === "year")?.value || "0000";
      const month = parts.find((p) => p.type === "month")?.value || "00";
      const day = parts.find((p) => p.type === "day")?.value || "00";
      const hour = parts.find((p) => p.type === "hour")?.value || "00";
      const minute = parts.find((p) => p.type === "minute")?.value || "00";
      const second = parts.find((p) => p.type === "second")?.value || "00";
      
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    } catch {
      return "N/A";
    }
  }, []);

  const formatDuration = useCallback((opened?: string | null, closed?: string | null): string => {
    if (!opened) {
      return "N/A";
    }
    const start = new Date(opened);
    const end = closed ? new Date(closed) : new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "N/A";
    }
    const diffMs = Math.max(end.getTime() - start.getTime(), 0);
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours <= 0 && remainingMinutes <= 0) {
      return "<1m";
    }
    if (hours <= 0) {
      return `${remainingMinutes}m`;
    }
    return `${hours}h ${remainingMinutes}m`;
  }, []);

  const formatPnlPercent = useCallback((pos: ActivePosition): string => {
    const entry = Number(pos.entry_price);
    const mark = Number(pos.mark_price);
    if (!Number.isFinite(entry) || !Number.isFinite(mark) || entry <= 0) {
      return "N/A";
    }
    const pct = pos.side === "short"
      ? ((entry - mark) / entry) * 100
      : ((mark - entry) / entry) * 100;
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${pct.toFixed(2)}%`;
  }, []);

  // Remaining lifetime of an open position before its max-duration timeout closes it.
  // Returns a formatted string ("2h 45m", "45m 12s", "12s", "Expired") or "N/A"
  // when the required fields are missing. Uses the live `now` clock for a countdown.
  const formatTimeLeft = useCallback((pos: ActivePosition): string => {
    const maxDur = Number(pos.max_duration_minutes);
    if (!Number.isFinite(maxDur) || maxDur <= 0) {
      return "N/A";
    }
    const opened = new Date(pos.opened_at);
    if (Number.isNaN(opened.getTime())) {
      return "N/A";
    }
    const deadlineMs = opened.getTime() + maxDur * 60_000;
    const remainingMs = deadlineMs - now.getTime();
    if (remainingMs <= 0) {
      return "Expired";
    }
    const totalSec = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [now]);

  // Fraction (0..1) of the max-duration already consumed. Used to color the
  // "Time Left" cell: green early, amber mid-life, red when close to expiry.
  const getTimeLeftRatio = useCallback((pos: ActivePosition): number | null => {
    const maxDur = Number(pos.max_duration_minutes);
    if (!Number.isFinite(maxDur) || maxDur <= 0) {
      return null;
    }
    const opened = new Date(pos.opened_at);
    if (Number.isNaN(opened.getTime())) {
      return null;
    }
    const elapsedMs = now.getTime() - opened.getTime();
    return Math.min(Math.max(elapsedMs / (maxDur * 60_000), 0), 1);
  }, [now]);


  const handleTradeTimeRangeChange = (value: TradeTimeRangeOption): void => {
    if (value === "custom") {
      updatePendingTradeFilters({ timeRange: value });
    } else {
      updatePendingTradeFilters({
        timeRange: value,
        startDate: "",
        endDate: "",
      });
    }
  };

  const handleTradeLimitChange = useCallback(
    (value: number): void => {
      setPendingTradeFilters((prev) => ({ ...prev, limit: value }));
      const appliedFilters = { ...tradeFilters, limit: value };
      void loadExecutedTrades(appliedFilters, 1, false);
    },
    [loadExecutedTrades, tradeFilters],
  );

  const handleTradeSymbolChange = (value: string): void => {
    updatePendingTradeFilters({ symbol: value });
  };

  const handleTradeStatusChange = (value: TradeFiltersState["status"]): void => {
    updatePendingTradeFilters({ status: value });
  };

  const handleTradeUserChange = (value: string): void => {
    updatePendingTradeFilters({ userId: value });
  };

  const handleTradeDateChange = (field: "startDate" | "endDate", value: string): void => {
    updatePendingTradeFilters({ [field]: value } as Partial<TradeFiltersState>);
  };


  async function loadCapitalManagementData(): Promise<void> {
    setIsLoadingCapitalData(true);
    try {
      // CRITICAL FIX: Always use force_refresh=true to get fresh data
      // This prevents displaying stale cached data (e.g., $57.17 instead of $28.79)
      const [state, heat, queue] = await Promise.all([
        getPortfolioState(true).catch(() => null), // force_refresh=true
        getPortfolioHeat().catch(() => null),
        getQueueLength().catch(() => ({ queue_length: 0 })),
      ]);

      if (state) {
        setPortfolioState(state);
      }
      if (heat) {
        setPortfolioHeat(heat);
      }
      if (queue) {
        setQueueLength(queue.queue_length);
      }
    } catch (err) {
      logger.error(
        "Error loading capital management data",
        err instanceof Error ? err : new Error(String(err)),
      );
    } finally {
      setIsLoadingCapitalData(false);
    }
  }


  async function loadRecommendationQueueData(): Promise<void> {
    setIsLoadingQueueData(true);
    try {
      const status = await getQueueStatus().catch(() => null);
      if (status) {
        setQueueStatus(status);
      }
    } catch (err) {
      logger.error(
        "Error loading recommendation queue data",
        err instanceof Error ? err : new Error(String(err)),
      );
    } finally {
      setIsLoadingQueueData(false);
    }
  }




  // Manual approval/rejection removed - system operates in AUTO mode only


  async function handleChooseSymbolsManually(): Promise<void> {
    setCustomPairsOpen(true);
  }


  async function handlePositionControl(
    positionId: string,
    action: "partial_exit" | "full_exit" | "adjust_stop" | "adjust_target",
    params?: { quantity?: number; stop_loss?: number; take_profit?: number[] }
  ): Promise<void> {
    setProcessingPositions((prev) => new Set(prev).add(positionId));
    try {
      const request: PositionControlRequest = {
        action,
        ...params,
      };
      await controlPosition(positionId, request);
      await queryClient.invalidateQueries({ queryKey: ["trading-system", "payload"] });
      showNotification(`Position ${action} executed successfully`, "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to control position";
      setError(message);
      showNotification(message, "error");
    } finally {
      setProcessingPositions((prev) => {
        const next = new Set(prev);
        next.delete(positionId);
        return next;
      });
    }
  }

  const highlightText = useMemo(() => {
    if (!payload) {
      return "";
    }
    return "Trading system is running. System will process recommendations based on configured settings.";
  }, [payload]);

  function renderRecommendationsSkeleton(): JSX.Element {
    return (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Intent</th>
              <th>Rationale</th>
              <th>Entry conditions</th>
              <th>Targets / stop</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((i) => (
              <tr key={i}>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "120px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "100px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "150px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "120px", height: "32px" }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderPositionsSkeleton(): JSX.Element {
    return (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Entry price</th>
              <th>Quantity</th>
              <th>Target</th>
              <th>Stop</th>
              <th>Unrealized PnL</th>
              <th>PnL %</th>
              <th>Time Left</th>
              <th>Manual controls</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2].map((i) => (
              <tr key={i}>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "100px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "100px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "200px", height: "32px" }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderTradesSkeleton(): JSX.Element {
    return (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>PnL</th>
              <th>Opened</th>
              <th>Closed</th>
              <th>Duration</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((i) => (
              <tr key={i}>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "140px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "140px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "80px", height: "20px" }} /></td>
                <td><Skeleton style={{ width: "120px", height: "20px" }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function renderRecommendationsTable(
    recommendations: TradingSystemPayload["trade_recommendations"],
    source: "news" | "platform",
    onViewDetails?: (recommendation: TradeRecommendation) => void,
    isLoading?: boolean,
  ): JSX.Element {
    // CRITICAL FIX: Show skeleton ONLY if payload doesn't exist AND we're loading
    // If payload exists, always show data (even if isLoading is true during refetch)
    if (isLoading && !payload) {
      return renderRecommendationsSkeleton();
    }
    
    // CRITICAL FIX: Ensure recommendations array exists and is valid
    // If recommendations is undefined/null, use empty array to prevent errors
    if (!recommendations || !Array.isArray(recommendations)) {
      return (
        <div className="table-card">
          <div className="empty-state">
            {source === "platform"
              ? "No platform-based recommendations available. Enable data trading and configure pair selection."
              : "No recommendations available."}
          </div>
        </div>
      );
    }
    
    // System always operates in AUTO mode - recommendations are automatically processed by Live Entry Gate

    let filteredRecommendations = recommendations.filter((r) => r.source === source);
    
    // CRITICAL FIX: Remove duplicate recommendations by symbol
    // Use shared helper function to avoid code duplication
    filteredRecommendations = deduplicateRecommendationsBySymbol(filteredRecommendations);
    
    if (sortConfig) {
      filteredRecommendations = [...filteredRecommendations].sort((a, b) => {
        let aVal: any;
        let bVal: any;
        
        if (sortConfig.field === "symbol") {
          aVal = a.symbol;
          bVal = b.symbol;
        } else if (sortConfig.field === "confidence") {
          aVal = a.confidence;
          bVal = b.confidence;
        } else if (sortConfig.field === "generated_at") {
          aVal = new Date(a.generated_at).getTime();
          bVal = new Date(b.generated_at).getTime();
        } else {
          return 0;
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    
    const total = filteredRecommendations.length;
    const startIndex = (pagination.page - 1) * pagination.pageSize;
    const paginatedRecommendations = filteredRecommendations.slice(startIndex, startIndex + pagination.pageSize);
    const hasRecommendations = paginatedRecommendations.length > 0;

    return (
      <div className="recommendations-grid">
        <div className="table-card" style={{ overflowX: "auto", maxWidth: "100%" }}>
          <table style={{ tableLayout: "auto", width: "100%", minWidth: "900px" }}>
            <thead>
              <tr>
                <th style={{ width: "80px", minWidth: "80px" }}>Symbol</th>
                <th style={{ width: "25%", minWidth: "200px" }}>Intent</th>
                <th style={{ width: "100px", minWidth: "100px" }}>Rationale</th>
                <th style={{ width: "140px", minWidth: "140px" }}>Entry conditions</th>
                <th style={{ width: "30%", minWidth: "250px" }}>Targets / stop</th>
                <th style={{ width: "160px", minWidth: "160px" }}>Expires</th>
              </tr>
            </thead>
            <tbody>
              {!hasRecommendations ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      "No platform-based recommendations available. Enable data trading and configure pair selection."
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {paginatedRecommendations.map((rec) => {
                    const formatExpirationDate = (expiresAt: string | null | undefined): string => {
                      if (!expiresAt) {
                        return "No expiration";
                      }
                      try {
                        const date = new Date(expiresAt);
                        if (Number.isNaN(date.getTime())) {
                          return "Invalid date";
                        }
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        const day = String(date.getDate()).padStart(2, "0");
                        const hours = String(date.getHours()).padStart(2, "0");
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        return `${year}-${month}-${day} ${hours}:${minutes}`;
                      } catch {
                        return "Invalid date";
                      }
                    };

                    const metConditions = rec.conditions.filter((c) => c.is_met);
                    const conditionsDisplay = rec.conditions.length > 0
                      ? rec.conditions.slice(0, 2).map((c, idx) => (
                          <div key={idx} style={{ fontSize: "0.75rem", color: c.is_met ? "var(--color-success)" : "var(--color-error)" }}>
                            {c.is_met ? "✓" : "✗"} {c.label}
                          </div>
                        ))
                      : null;

                    return (
                      <tr key={rec.id}>
                        <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "top" }}>{rec.symbol}</td>
                        <td style={{ padding: "0.75rem 0.75rem", verticalAlign: "top", wordBreak: "break-word" }}>
                          <div style={{ lineHeight: "1.5" }}>{rec.summary}</div>
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "top", whiteSpace: "nowrap" }}>
                          Confidence: {(rec.confidence * 100).toFixed(1)}%
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "top" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <div style={{ fontWeight: 500 }}>
                              {metConditions.length} / {rec.conditions.length} met
                            </div>
                            {conditionsDisplay}
                            {rec.conditions.length > 2 && (
                              <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>
                                +{rec.conditions.length - 2} more
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem 0.75rem", verticalAlign: "top" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <div style={{ lineHeight: "1.4" }}>
                              TP: {rec.take_profit.join(", ")} | SL: {rec.stop_loss}
                            </div>
                            {(rec.entry_min || rec.entry_max || rec.entry_optimal) && (
                              <div style={{ fontSize: "0.75rem", color: "var(--color-info)", marginTop: "2px", lineHeight: "1.3" }}>
                                Envelope: {rec.entry_min?.toFixed(4) || "N/A"} - {rec.entry_max?.toFixed(4) || "N/A"}
                                {rec.entry_optimal && (
                                  <span style={{ marginLeft: "4px" }}>
                                    (Optimal: {rec.entry_optimal.toFixed(4)})
                                  </span>
                                )}
                              </div>
                            )}
                            {rec.refresh_count !== undefined && rec.refresh_count > 0 && (
                              <div style={{ fontSize: "0.75rem", color: "var(--color-warning)", marginTop: "2px", lineHeight: "1.3" }}>
                                Refreshed {rec.refresh_count} time{rec.refresh_count > 1 ? "s" : ""}
                              </div>
                            )}
                            {rec.current_rr_ratio !== undefined && rec.current_rr_ratio !== null && (
                              <div style={{ fontSize: "0.75rem", color: "var(--color-success)", marginTop: "2px", lineHeight: "1.3" }}>
                                Current RR: {rec.current_rr_ratio.toFixed(2)}
                                {rec.goal_achievement_pct !== undefined && rec.goal_achievement_pct !== null && (
                                  <span style={{ marginLeft: "8px" }}>
                                    | Goal: {rec.goal_achievement_pct.toFixed(1)}%
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", verticalAlign: "top" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px", alignItems: "flex-start" }}>
                            <div style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", whiteSpace: "nowrap" }}>
                              {formatExpirationDate(rec.expires_at)}
                            </div>
                            {onViewDetails && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onViewDetails(rec);
                                }}
                              >
                                View Details
                              </Button>
                            )}
                          </div>
                        </td>
                  </tr>
                    );
                  })}
                  {total > pagination.pageSize && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: "center", padding: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "center", gap: "8px", alignItems: "center" }}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() => setPagination({ ...pagination, page: Math.max(1, pagination.page - 1) })}
                            disabled={pagination.page === 1}
                          >
                            Previous
                          </Button>
                          <span>
                            Page {pagination.page} of {Math.ceil(total / pagination.pageSize)} ({total} total)
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() => setPagination({ ...pagination, page: Math.min(Math.ceil(total / pagination.pageSize), pagination.page + 1) })}
                            disabled={pagination.page >= Math.ceil(total / pagination.pageSize)}
                          >
                            Next
                          </Button>
                          <select
                            value={pagination.pageSize}
                            onChange={(e) => setPagination({ page: 1, pageSize: parseInt(e.target.value) })}
                            style={{ padding: "4px", borderRadius: "4px", border: "1px solid var(--color-border)" }}
                          >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                            <option value={100}>100 per page</option>
                          </select>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "8px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Button
            type="button"
            variant="ghost"
            size="small"
            onClick={() => setSortConfig(sortConfig?.field === "symbol" && sortConfig.direction === "asc" ? { field: "symbol", direction: "desc" } : { field: "symbol", direction: "asc" })}
          >
            Sort by Symbol {sortConfig?.field === "symbol" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="small"
            onClick={() => setSortConfig(sortConfig?.field === "confidence" && sortConfig.direction === "asc" ? { field: "confidence", direction: "desc" } : { field: "confidence", direction: "asc" })}
          >
            Sort by Confidence {sortConfig?.field === "confidence" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="small"
            onClick={() => setSortConfig(sortConfig?.field === "generated_at" && sortConfig.direction === "asc" ? { field: "generated_at", direction: "desc" } : { field: "generated_at", direction: "asc" })}
          >
            Sort by Date {sortConfig?.field === "generated_at" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </Button>
          {sortConfig && (
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => setSortConfig(null)}
            >
              Clear Sort
            </Button>
          )}
        </div>
      </div>
    );
  }


  function renderPositionsTable(
    positions: TradingSystemPayload["active_positions"],
    onControl: (
      id: string,
      action: PositionControlRequest["action"],
      params?: { quantity?: number; stop_loss?: number; take_profit?: number[] },
    ) => void,
    processingIds: Set<string>,
    onPositionControlClick: (position: ActivePosition) => void,
    isLoading?: boolean,
    isAdmin?: boolean,
  ): JSX.Element {
    // CRITICAL FIX: Show skeleton ONLY if payload doesn't exist AND we're loading
    // If payload exists, always show data (even if isLoading is true during refetch)
    if (isLoading && !payload) {
      return renderPositionsSkeleton();
    }
    
    // CRITICAL FIX: Ensure positions array exists and is valid
    // If positions is undefined/null, use empty array to prevent errors
    if (!positions || !Array.isArray(positions)) {
      return (
        <div className="table-card">
          <div className="empty-state">No active positions</div>
        </div>
      );
    }

    // Format a price level with adaptive precision: up to 6 decimals for
    // normal assets, up to 8 for very small unit prices (e.g. SHIB-like tokens).
    const formatLevel = (v: number | null | undefined): string => {
      const n = Number(v);
      if (v == null || !Number.isFinite(n)) return "—";
      const abs = Math.abs(n);
      if (abs >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
      return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
    };

    // Format quantity with compact precision (up to 6 decimals) so values
    // like 17.686039304563362 render as 17.686039 instead of a long tail.
    const formatQuantity = (q: number): string => {
      const n = Number(q);
      if (!Number.isFinite(n)) return "—";
      return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
    };

    // Compute the signed percentage distance from entry to a price level,
    // respecting the position side so the sign reads as gain/loss from the
    // trader's perspective (positive = favorable move, negative = against).
    const pctFromEntry = (level: number | null | undefined, side: string, entry: number): string | null => {
      const lvl = Number(level);
      const e = Number(entry);
      if (level == null || !Number.isFinite(lvl) || !Number.isFinite(e) || e <= 0) return null;
      const dist = side === "short" ? ((e - lvl) / e) * 100 : ((lvl - e) / e) * 100;
      const sign = dist >= 0 ? "+" : "";
      return `${sign}${dist.toFixed(2)}%`;
    };

    // Render a price level followed by its percentage distance from entry,
    // e.g. "0.769317 (+1.03%)" so the target/stop reads naturally.
    const formatLevelWithPct = (level: number | null | undefined, side: string, entry: number): string => {
      const pct = pctFromEntry(level, side, entry);
      return pct == null ? "—" : `${formatLevel(level)} (${pct})`;
    };


    return (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Entry price</th>
              <th>Quantity</th>
              <th>Target</th>
              <th>Stop</th>
              <th>Unrealized PnL</th>
              <th>PnL %</th>
              <th>Time Left</th>
              <th>Manual controls</th>
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="empty-state">
                    No active positions. Positions will appear here once trades are executed.
                  </div>
                </td>
              </tr>
            ) : (
              positions.flatMap((pos) => {
                const isExpanded = expandedAIPositions.has(pos.id);
                const toggleExpanded = () => {
                  setExpandedAIPositions((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(pos.id)) {
                      newSet.delete(pos.id);
                    } else {
                      newSet.add(pos.id);
                    }
                    return newSet;
                  });
                };

                return [
                  <tr key={pos.id}>
                    <td>{pos.symbol}</td>
                    <td>{pos.entry_price}</td>
                    <td>{formatQuantity(pos.quantity)}</td>
                    <td title={(pos.take_profit ?? []).join(", ")}>
                      {formatLevelWithPct(pos.take_profit?.[0], pos.side, pos.entry_price)}
                    </td>
                    <td title={pos.stop_loss != null ? String(pos.stop_loss) : "N/A"}>
                      {formatLevelWithPct(pos.stop_loss, pos.side, pos.entry_price)}
                    </td>
                    <td>{(pos.unrealized_pnl ?? 0).toFixed(2)}</td>
                    <td
                      className={
                        (() => {
                          const entry = Number(pos.entry_price);
                          const mark = Number(pos.mark_price);
                          const gain = pos.side === "short" ? entry - mark : mark - entry;
                          if (gain > 0) return "trade-pnl trade-pnl--positive";
                          if (gain < 0) return "trade-pnl trade-pnl--negative";
                          return "trade-pnl";
                        })()
                      }
                    >
                      {formatPnlPercent(pos)}
                    </td>
                    <td
                      className={
                        (() => {
                          const ratio = getTimeLeftRatio(pos);
                          if (ratio === null) return "time-left";
                          if (ratio >= 0.85) return "time-left time-left--critical";
                          if (ratio >= 0.5) return "time-left time-left--warning";
                          return "time-left";
                        })()
                      }
                      title={`Opened ${formatDateTime(pos.opened_at)} · Max ${pos.max_duration_minutes ?? "N/A"}m`}
                    >
                      {formatTimeLeft(pos)}
                    </td>
                    <td>
                      <AIDecisionBadgeWithExpanded
                        position={pos}
                        expanded={isExpanded}
                        onToggle={toggleExpanded}
                      />
                    </td>
                    <td>
                      {isAdmin ? (
                        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() => onPositionControlClick(pos)}
                            disabled={processingIds.has(pos.id)}
                          >
                            Adjust Stop/Target
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() =>
                              onControl(pos.id, "partial_exit", {
                                quantity: pos.quantity * 0.5,
                              })
                            }
                            disabled={processingIds.has(pos.id)}
                          >
                            Partial Exit
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="small"
                            onClick={() => onControl(pos.id, "full_exit")}
                            disabled={processingIds.has(pos.id)}
                          >
                            Full Exit
                          </Button>
                        </div>
                      ) : (
                        <span style={{ color: "var(--color-text-secondary)", fontSize: "0.875rem" }}>
                          Admin only
                        </span>
                      )}
                    </td>
                  </tr>,
                  isExpanded && (
                    <ExpandedAIAnalysisRow
                      key={`expanded-${pos.id}`}
                      positionId={pos.id}
                      onClose={toggleExpanded}
                    />
                  ),
                ].filter(Boolean);
              })
            )}
          </tbody>
        </table>
      </div>
    );
  }

  function renderTradeFiltersForm(): JSX.Element {
    const isAtDefaults = JSON.stringify(tradeFilters) === JSON.stringify(DEFAULT_TRADE_FILTERS);

    return (
      <div className="trade-filters">
        <div className="trade-filters__row">
          <div className="trade-filters__field">
            <label>Time range</label>
            <select
              value={pendingTradeFilters.timeRange}
              onChange={(event) => handleTradeTimeRangeChange(event.target.value as TradeTimeRangeOption)}
            >
              {Object.entries(TIME_RANGE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="trade-filters__field">
            <label>Outcome</label>
            <select
              value={pendingTradeFilters.status}
              onChange={(event) => handleTradeStatusChange(event.target.value as TradeFiltersState["status"])}
            >
              {TRADE_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="trade-filters__field">
            <label>Symbol</label>
            <input
              type="text"
              placeholder="e.g. BTC/USDT"
              value={pendingTradeFilters.symbol}
              onChange={(event) => handleTradeSymbolChange(event.target.value)}
            />
          </div>
          {isAdmin && (
            <div className="trade-filters__field">
              <label>User scope</label>
              <select
                value={pendingTradeFilters.userId}
                onChange={(event) => handleTradeUserChange(event.target.value)}
                disabled={isLoadingExecutedTrades || isLoadingAdminTradeUsers}
              >
                <option value="">
                  {currentUser?.email ? `My trades (${currentUser.email})` : "My trades"}
                </option>
                <option value="__all__">
                  All users
                </option>
                {adminTradeUserOptions.map((tradeUser: AdminUser) => (
                  <option key={tradeUser.id} value={tradeUser.id}>
                    {tradeUser.name ? `${tradeUser.email} (${tradeUser.name})` : tradeUser.email}
                  </option>
                ))}
              </select>
              <span className="trade-filters__hint">
                Leave on your account or choose a specific user.
              </span>
            </div>
          )}
          <div className="trade-filters__field">
            <label>Results per page</label>
            <select
              value={pendingTradeFilters.limit}
              onChange={(event) => handleTradeLimitChange(Number(event.target.value))}
              disabled={isLoadingExecutedTrades}
            >
              {TRADE_LIMIT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span className="trade-filters__hint">Applies immediately</span>
          </div>
        </div>

        {pendingTradeFilters.timeRange === "custom" && (
          <div className="trade-filters__row trade-filters__row--stacked">
            <div className="trade-filters__field">
              <label>Start date</label>
              <input
                type="datetime-local"
                value={pendingTradeFilters.startDate}
                onChange={(event) => handleTradeDateChange("startDate", event.target.value)}
              />
            </div>
            <div className="trade-filters__field">
              <label>End date</label>
              <input
                type="datetime-local"
                value={pendingTradeFilters.endDate}
                onChange={(event) => handleTradeDateChange("endDate", event.target.value)}
              />
            </div>
          </div>
        )}

        <div className="trade-filters__actions">
          <div className="trade-filters__buttons">
            <Button
              type="button"
              variant="primary"
              onClick={handleApplyTradeFilters}
              disabled={isLoadingExecutedTrades || !tradeFiltersDirty}
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetTradeFilters}
              disabled={isLoadingExecutedTrades || isAtDefaults}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleRefreshExecutedTrades}
              disabled={isLoadingExecutedTrades}
            >
              Refresh
            </Button>
          </div>
          <div className="trade-filters__meta">
            <span>Last updated: {tradesLastUpdatedAt ? formatDateTime(tradesLastUpdatedAt) : "—"}</span>
            <span>Results: {executedTradesMeta.total}</span>
          </div>
        </div>

        {appliedTradeFilters && (appliedTradeFilters.start || appliedTradeFilters.end) && (
          <div className="trade-filters__applied">
            Showing trades from {formatDateTime(appliedTradeFilters.start ?? null)} to{" "}
            {formatDateTime(appliedTradeFilters.end ?? null)}
          </div>
        )}
        {isAdmin && activeTradeUserLabel && (
          <div className="trade-filters__applied">Viewing trades for {activeTradeUserLabel}</div>
        )}
      </div>
    );
  }

  function renderExecutedTradesPanel(): JSX.Element {
    const totalPages = Math.max(
      1,
      Math.ceil(
        executedTradesMeta.total > 0
          ? executedTradesMeta.total / Math.max(executedTradesMeta.pageSize, 1)
          : 1
      )
    );

    return (
      <div className="trade-results-panel">
        {renderTradeFiltersForm()}
        <div className="trade-results-panel__table-section">
          <div className="trade-results-panel__table-controls">
            <div className="trade-results-panel__pagination">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleTradePageChange("prev")}
                disabled={isLoadingExecutedTrades || executedTradesMeta.page === 1}
              >
                Previous
              </Button>
              <span>
                Page {executedTradesMeta.page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleTradePageChange("next")}
                disabled={isLoadingExecutedTrades || !executedTradesMeta.hasNext}
              >
                Next
              </Button>
            </div>
          </div>
          {executedTradesError && <div className="trade-filters__error">{executedTradesError}</div>}
          {renderTradesTable(executedTrades, isLoadingExecutedTrades)}
        </div>
      </div>
    );
  }

  function renderTradesTable(trades: ExecutedTrade[], isLoading?: boolean): JSX.Element {
    if (isLoading) {
      return renderTradesSkeleton();
    }

    return (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Entry Price</th>
              <th>Exit Price</th>
              <th>PnL</th>
              <th>PnL %</th>
              <th title="Exit fees: actual or estimated 0.2% of trade value">Fees*</th>
              <th>Opened</th>
              <th>Closed</th>
              <th>Duration</th>
              <th>Slot budget</th>
              <th className="notes-col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 ? (
              <tr>
                <td colSpan={11}>
                  <div className="empty-state">
                    No trades have been executed yet. Results will populate once the system starts trading.
                  </div>
                </td>
              </tr>
            ) : (
              trades.map((trade) => (
                <tr key={trade.id}>
                  <td>{trade.symbol}</td>
                  <td>
                    {typeof trade.entry_price === "number" ? trade.entry_price.toFixed(8) : "—"}
                  </td>
                  <td>
                    {typeof trade.exit_price === "number" ? trade.exit_price.toFixed(8) : "—"}
                  </td>
                  <td
                    className={
                      trade.net_pnl !== null && trade.net_pnl > 0
                        ? "trade-pnl trade-pnl--positive"
                        : trade.net_pnl !== null && trade.net_pnl < 0
                        ? "trade-pnl trade-pnl--negative"
                        : "trade-pnl"
                    }
                  >
                    {trade.net_pnl !== null ? trade.net_pnl.toFixed(2) : "—"}
                  </td>
                  <td
                    className={
                      trade.net_pnl !== null && trade.net_pnl > 0
                        ? "trade-pnl trade-pnl--positive"
                        : trade.net_pnl !== null && trade.net_pnl < 0
                        ? "trade-pnl trade-pnl--negative"
                        : "trade-pnl"
                    }
                  >
                    {(() => {
                      if (trade.net_pnl === null) return "—";
                      if (
                        typeof trade.entry_price === "number" &&
                        trade.entry_price > 0 &&
                        typeof trade.quantity === "number" &&
                        trade.quantity > 0
                      ) {
                        const pnlPercent = (trade.net_pnl / (trade.entry_price * trade.quantity)) * 100;
                        return `${pnlPercent >= 0 ? "+" : ""}${pnlPercent.toFixed(2)}%`;
                      }
                      return "—";
                    })()}
                  </td>
                  <td
                    className="trade-pnl"
                    title="Total exchange fees in USDT"
                  >
                    {typeof trade.fees_usdt === "number" ? `$${trade.fees_usdt.toFixed(4)}` : "—"}
                  </td>
                  <td>{formatDateTime(trade.opened_at)}</td>
                  <td>{formatDateTime(trade.closed_at)}</td>
                  <td>{formatDuration(trade.opened_at, trade.closed_at)}                  </td>
                  <td title={trade.slot_allocation_usd ? "Capital reserved for this slot" : undefined}>
                    {typeof trade.slot_allocation_usd === "number" ? `$${trade.slot_allocation_usd.toFixed(2)}` : "—"}
                  </td>
                  <td className="notes-col">{trade.notes || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  function renderTradesReport(report: TradesReport | null | undefined): JSX.Element {
    if (!report) {
      return (
        <div className="empty-state">
          No trades report available. Reports will be generated once trades are executed.
        </div>
      );
    }

    return (
      <div className="risk-summary-grid">
        <div className="risk-card">
          <strong>Total Trades</strong>
          <span>{report.total_trades}</span>
        </div>
        <div className="risk-card">
          <strong>Win Rate</strong>
          <span>{(report.win_rate ?? 0) * 100 > 0 ? `${((report.win_rate ?? 0) * 100).toFixed(1)}%` : 'N/A'}</span>
        </div>
        <div className="risk-card">
          <strong>Total PnL</strong>
          <span style={{ color: (report.total_pnl ?? 0) >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
            {(report.total_pnl ?? 0) >= 0 ? "+$" : "-$"}
            {Math.abs(report.total_pnl ?? 0).toFixed(2)}
          </span>
          <span
            style={{
              fontSize: "0.8rem",
              color: (report.total_return_percent ?? 0) >= 0 ? "var(--color-success)" : "var(--color-error)",
            }}
          >
            {(report.total_return_percent ?? 0) >= 0 ? "+" : ""}
            {(report.total_return_percent ?? 0).toFixed(2)}%
          </span>
        </div>
        <div className="risk-card">
          <strong>Winning Trades</strong>
          <span>{report.winning_trades ?? 0}</span>
        </div>
        <div className="risk-card">
          <strong>Losing Trades</strong>
          <span>{report.losing_trades ?? 0}</span>
        </div>
        <div className="risk-card">
          <strong title="0.2% per trade when actual fees unavailable">Est. Fees*</strong>
          <span style={{ color: "var(--color-error)" }}>
             {(report.total_fees ?? 0) > 0 ? `-$${(report.total_fees ?? 0).toFixed(2)}` : "$0.00"}
          </span>
        </div>
        <div className="risk-card">
          <strong>Average Win</strong>
          <span style={{ color: "var(--color-success)" }}>
            +{(report.average_win ?? 0).toFixed(4)}
          </span>
        </div>
        <div className="risk-card">
          <strong>Average Loss</strong>
          <span style={{ color: "var(--color-error)" }}>
            {(report.average_loss ?? 0).toFixed(4)}
          </span>
        </div>
        <div className="risk-card">
          <strong>Best Trade</strong>
          <span style={{ color: "var(--color-success)" }}>
            {report.best_trade
              ? `${report.best_trade.symbol}: +${(report.best_trade.net_pnl ?? 0).toFixed(2)}`
              : "N/A"}
          </span>
        </div>
        {Object.keys(report.trades_by_source ?? {}).length > 0 && (
          <div className="risk-card" style={{ gridColumn: "1 / -1" }}>
            <strong>Trades by Source</strong>
            <div style={{ marginTop: "8px" }}>
              {Object.entries(report.trades_by_source ?? {}).map(([source, stats]) => (
                <div key={source} style={{ display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                  <span>{source === "platform" ? "Platform" : source}:</span>
                  <span>
                    {stats.winning ?? 0}/{stats.total ?? 0} wins, PnL: {(stats.total_pnl ?? 0) >= 0 ? "+" : ""}
                    {(stats.total_pnl ?? 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  function showNotification(message: string, type: "success" | "error" | "warning" | "info"): void {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  }

  function renderNotification(): JSX.Element | null {
    if (!notification) {
      return null;
    }

    const notificationStyle: React.CSSProperties = {
      position: "fixed",
      top: "20px",
      right: "20px",
      zIndex: 2000,
      padding: "12px 16px",
      borderRadius: "6px",
      minWidth: "280px",
      maxWidth: "400px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      borderLeft: "4px solid",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      animation: "slideInRight 0.3s ease-out",
    };

    const typeStyles = {
      success: {
        background: "var(--color-success-light)",
        color: "var(--color-success)",
        borderLeftColor: "var(--color-success)",
      },
      error: {
        background: "var(--color-error-light)",
        color: "var(--color-error)",
        borderLeftColor: "var(--color-error)",
      },
      warning: {
        background: "var(--color-warning-light)",
        color: "var(--color-warning)",
        borderLeftColor: "var(--color-warning)",
      },
      info: {
        background: "var(--color-info-light)",
        color: "var(--color-info)",
        borderLeftColor: "var(--color-info)",
      },
    };

    const icons = {
      success: "✓",
      error: "✕",
      warning: "⚠",
      info: "ℹ",
    };

    return (
      <div style={{ ...notificationStyle, ...typeStyles[notification.type] }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
          <span style={{ fontSize: "1.2rem" }}>{icons[notification.type]}</span>
          <span style={{ fontSize: "0.875rem", lineHeight: "1.4" }}>{notification.message}</span>
        </div>
        <button
          type="button"
          onClick={() => setNotification(null)}
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            padding: "4px",
            fontSize: "1.2rem",
            lineHeight: "1",
            opacity: 0.7,
          }}
          aria-label="Close notification"
        >
          ×
        </button>
      </div>
    );
  }

  function renderRiskSummary(risk: TradingSystemPayload["risk"]): JSX.Element {
    return (
      <div className="risk-summary-grid">
        <div className="risk-card">
          <strong>Portfolio Value</strong>
          <span>{(risk.portfolio_value ?? 0).toFixed(2)}</span>
        </div>
        <div className="risk-card">
          <strong>Daily PnL</strong>
          <span>{(risk.daily_pnl ?? 0).toFixed(2)}</span>
        </div>
        <div className="risk-card">
          <strong>Exposure %</strong>
          <span>{(risk.exposure_pct ?? 0).toFixed(2)}%</span>
        </div>
        <div className="risk-card">
          <strong>Value at Risk</strong>
          <span>
            {risk.value_at_risk != null ? Number(risk.value_at_risk).toFixed(2)               : "N/A"}
          </span>
        </div>
        <div
          style={{
            fontSize: "0.7rem",
            color: "rgba(203,213,225,0.4)",
            marginTop: "8px",
            gridColumn: "1 / -1"
          }}
        >
          * Fees estimated at 0.2% of trade value for trades where actual exchange fees were not captured.
        </div>
      </div>
    );
  }

  function renderWarnings(warnings: Array<{ id: string; type: "error" | "warning" | "info"; message: string; isAdminNotification: boolean; notification?: AdminNotification }>): JSX.Element | null {
    if (!warnings || warnings.length === 0) {
      return null;
    }

    const criticalCount = warnings.filter((w) => w.type === "error").length;
    const warningCount = warnings.filter((w) => w.type === "warning").length;
    const infoCount = warnings.filter((w) => w.type === "info").length;

    const getWarningIcon = (type: "error" | "warning" | "info"): string => {
      if (type === "error") return "🔴";
      if (type === "warning") return "🟡";
      return "🔵";
    };

    const getWarningColor = (type: "error" | "warning" | "info"): string => {
      if (type === "error") return "var(--color-error)";
      if (type === "warning") return "var(--color-warning)";
      return "var(--color-info)";
    };

    const handleMouseEnter = () => {
      if (warningsDropdownTimeout) {
        clearTimeout(warningsDropdownTimeout);
        setWarningsDropdownTimeout(null);
      }
      setWarningsDropdownOpen(true);
    };

    const handleMouseLeave = () => {
      const timeout = setTimeout(() => {
        setWarningsDropdownOpen(false);
      }, 200);
      setWarningsDropdownTimeout(timeout);
    };

    return (
      <div 
        style={{ position: "relative", marginBottom: "16px" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            borderRadius: "6px",
            background: criticalCount > 0 
              ? "rgba(239, 68, 68, 0.1)" 
              : warningCount > 0 
              ? "rgba(234, 179, 8, 0.1)" 
              : "rgba(59, 130, 246, 0.1)",
            border: `1px solid ${criticalCount > 0 ? "rgba(239, 68, 68, 0.3)" : warningCount > 0 ? "rgba(234, 179, 8, 0.3)" : "rgba(59, 130, 246, 0.3)"}`,
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onClick={() => setWarningsDropdownOpen(!warningsDropdownOpen)}
        >
          <span style={{ fontSize: "1rem" }}>
            {criticalCount > 0 ? "🔴" : warningCount > 0 ? "🟡" : "🔵"}
          </span>
          <span style={{ fontSize: "0.875rem", fontWeight: "500", flex: 1 }}>
            {criticalCount > 0 
              ? `${criticalCount} Critical Alert${criticalCount > 1 ? "s" : ""}`
              : warningCount > 0 
              ? `${warningCount} Warning${warningCount > 1 ? "s" : ""}`
              : `${infoCount} Notification${infoCount > 1 ? "s" : ""}`}
          </span>
          <span style={{ 
            fontSize: "0.75rem", 
            padding: "2px 6px", 
            borderRadius: "12px",
            background: criticalCount > 0 
              ? "rgba(239, 68, 68, 0.2)" 
              : warningCount > 0 
              ? "rgba(234, 179, 8, 0.2)" 
              : "rgba(59, 130, 246, 0.2)",
            color: getWarningColor(criticalCount > 0 ? "error" : warningCount > 0 ? "warning" : "info"),
            fontWeight: "600",
          }}>
            {warnings.length}
          </span>
          <span style={{ 
            fontSize: "0.75rem",
            transition: "transform 0.2s ease",
            transform: warningsDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}>
            ▼
          </span>
        </div>

        {warningsDropdownOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              marginTop: "4px",
              padding: "8px",
              borderRadius: "6px",
              background: "rgba(30, 41, 59, 0.98)",
              border: "2px solid rgba(148, 163, 184, 0.3)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(148, 163, 184, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
              zIndex: 1000,
              maxHeight: "300px",
              overflowY: "auto",
              backdropFilter: "blur(10px)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {warnings.map((warning) => {
                const type = warning.type;
                // Support dismiss for both admin and user notifications
                let handleDismiss: (() => void) | undefined;
                if (warning.isAdminNotification && warning.notification) {
                  handleDismiss = () => {
                    setAcknowledgedNotifications((prev) => new Set([...prev, warning.id]));
                    acknowledgeMutation.mutate(warning.id);
                  };
                } else if ((warning as any).isUserNotification && (warning as any).notification) {
                  handleDismiss = () => {
                    setDismissedUserNotifications((prev) => new Set([...prev, warning.id]));
                    markAsReadMutation.mutate(warning.id);
                  };
                }
                
                return (
                  <div
                    key={warning.id}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "4px",
                      borderLeft: `3px solid ${getWarningColor(type)}`,
                      background: type === "error" 
                        ? "rgba(239, 68, 68, 0.05)" 
                        : type === "warning" 
                        ? "rgba(234, 179, 8, 0.05)" 
                        : "rgba(59, 130, 246, 0.05)",
                      fontSize: "0.8rem",
                      color: "var(--color-text)",
                      lineHeight: "1.4",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <span style={{ marginRight: "6px" }}>{getWarningIcon(type)}</span>
                      {warning.message}
                    </div>
                    {handleDismiss && (
                      <button
                        type="button"
                        onClick={handleDismiss}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "var(--color-text)",
                          cursor: "pointer",
                          padding: "2px 4px",
                          fontSize: "1rem",
                          lineHeight: "1",
                          opacity: 0.7,
                          flexShrink: 0,
                        }}
                        aria-label="Dismiss notification"
                        title="Dismiss notification"
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  const engineModeLabel = useMemo(() => {
    const mode = (payload?.engine_mode) || "rule_based";
    switch (mode) {
      case "ai_technical":
        return "AI Technical";
      case "ai_orderbook":
        return "AI Orderbook";
      default:
        return "Rule-Based";
    }
  }, [payload?.engine_mode]);

  const engineModeColor = useMemo(() => {
    const mode = (payload?.engine_mode) || "rule_based";
    switch (mode) {
      case "ai_technical":
        return "#a855f7";
      case "ai_orderbook":
        return "#f59e0b";
      default:
        return "#3b82f6";
    }
  }, [payload?.engine_mode]);

  const engineModeBg = useMemo(() => {
    const mode = (payload?.engine_mode) || "rule_based";
    switch (mode) {
      case "ai_technical":
        return "rgba(168, 85, 247, 0.15)";
      case "ai_orderbook":
        return "rgba(245, 158, 11, 0.15)";
      default:
        return "rgba(59, 130, 246, 0.15)";
    }
  }, [payload?.engine_mode]);

  // Show skeleton loader only while loading initial data (no payload available)
  // Do not show skeleton during refresh if payload exists - show data with loading overlay instead
  if (isLoading && !payload) {
    return (
      <div className="trading-system-root">
        <div style={{ padding: "24px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            <Skeleton style={{ width: "200px", height: "24px" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "24px" }}>
            <Card className="overview-card">
              <Skeleton style={{ width: "100%", height: "120px" }} />
            </Card>
            <Card className="overview-card">
              <Skeleton style={{ width: "100%", height: "120px" }} />
            </Card>
          </div>
          <Card className="overview-card">
            <Skeleton style={{ width: "100%", height: "300px" }} />
          </Card>
        </div>
      </div>
    );
  }

  // Show error state only if no payload is available (complete failure)
  const displayError = error || (queryError && !payload ? (queryError instanceof Error ? queryError.message : String(queryError)) : null);
  if (displayError && !payload) {
    return (
      <div className="trading-system-root">
        <div style={{ padding: "24px", maxWidth: "600px", margin: "0 auto" }}>
          <div
            style={{
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid var(--color-error)",
              background: "rgba(239, 68, 68, 0.1)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "3rem",
                marginBottom: "16px",
              }}
            >
              ⚠️
            </div>
            <div
              style={{
                fontWeight: "600",
                fontSize: "1.25rem",
                marginBottom: "8px",
                color: "var(--color-error)",
              }}
            >
              Error loading trading system
            </div>
            <div
              style={{
                color: "var(--color-text-secondary)",
                marginBottom: "24px",
                lineHeight: "1.5",
              }}
            >
              {displayError}
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              {isLoading ? "Retrying..." : "Retry"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="trading-system-root">
        <div className="empty-state">
          <div>No data available</div>
          <Button
            type="button"
            variant="primary"
            onClick={() => refetch()}
            style={{ marginTop: "16px" }}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }
  
  // CRITICAL FIX: Ensure payload has required arrays initialized
  // Prevent undefined/null errors when accessing payload properties
  const safePayload = {
    ...payload,
    settings: payload.settings || {
      daily_trade_limit: 10,
      last_updated_at: new Date().toISOString(),
    },
    trade_recommendations: Array.isArray(payload.trade_recommendations) ? payload.trade_recommendations : [],
    active_positions: Array.isArray(payload.active_positions) ? payload.active_positions : [],
    executed_trades: Array.isArray(payload.executed_trades) ? payload.executed_trades : [],
    warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
    custom_pairs: Array.isArray(payload.custom_pairs) ? payload.custom_pairs : [],
  };

  const tradingMode = safePayload.settings?.trading_mode === "live" ? "Live" : "Paper";

  // Control mode (disabled / manual / auto). Falls back to AUTO when absent so the
  // supervision toggle never renders an undefined state.
  const dataTradingMode: TradingMode =
    payload?.data_trading_mode === "manual" || payload?.data_trading_mode === "disabled"
      ? payload.data_trading_mode
      : "auto";

  return (
    <div className="trading-system-root">
      {renderNotification()}
      <section className="trading-system-hero">
        <div className="trading-system-status">
          <div>
            <span className="app-header__eyebrow">Operational control</span>
            <h2>Trading System</h2>
          </div>
          <div className="trading-system-status__meta" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span className="trading-system-mode-badge">Mode: {tradingMode}</span>
            <span className="trading-system-mode-badge" style={{ borderColor: engineModeColor, color: engineModeColor, background: engineModeBg }}>Engine: {engineModeLabel}</span>
          </div>
          <div className="trading-system-highlight">{highlightText}</div>
        </div>
        <div className="trading-system-controls">
          <Button
            type="button"
            variant="ghost"
            onClick={loadPayload}
            disabled={isLoading || isFetching}
          >
            {isLoading || isFetching ? "Refreshing..." : "Refresh"}
          </Button>
          {payload.last_refreshed_at && (
            <span style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)" }}>
              Last refresh: {formatDateTime(payload.last_refreshed_at)}
            </span>
          )}
        </div>
      </section>

      {/* Error Banner with Retry Button */}
      {queryError && (
        <div
          style={{
            marginBottom: "16px",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid var(--color-error)",
            background: "rgba(239, 68, 68, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
                fontWeight: "600",
                color: "var(--color-error)",
              }}
            >
              <span>⚠️</span>
              <span>Failed to load trading system data</span>
            </div>
            <div
              style={{
                fontSize: "0.875rem",
                color: "var(--color-text-secondary)",
                marginTop: "4px",
              }}
            >
              {queryError instanceof Error
                ? queryError.message
                : typeof queryError === "string"
                ? queryError
                : "An unexpected error occurred. Please try again."}
            </div>
          </div>
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={() => {
              refetch();
            }}
            disabled={isLoading}
          >
            {isLoading ? "Retrying..." : "Retry"}
          </Button>
        </div>
      )}

      {allWarnings.length > 0 && renderWarnings(allWarnings)}


      <section className="trading-system-grid">
        <Card
          className="overview-card trading-system-card"
          title="Platform and portfolio intelligence"
          subtitle="Surface ideas derived from order books, liquidity flows, and portfolio analytics."
        >
          <div className="recommendations-grid">
            <div className="recommendations-header">
              <div className="stats-inline">
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span>
                    Pair selection: <strong>Smart AI</strong>
                    {payload.custom_pairs.length > 0 && (
                      <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                        ({payload.custom_pairs.length} priority pairs)
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>
                    AI automatically selects pairs based on liquidity, volatility, and technical signals
                    {payload.custom_pairs.length > 0 && ". Custom pairs are evaluated first with priority."}
                  </span>
                </div>
              </div>
              {isAdmin && (
                <div className="recommendations-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleChooseSymbolsManually}
                    disabled={false}
                    title="Add priority trading pairs. Smart AI will continue selecting pairs automatically."
                  >
                    {payload.custom_pairs.length > 0 ? `Edit Priority Pairs (${payload.custom_pairs.length})` : "Add Priority Pairs"}
                  </Button>
                </div>
              )}
            </div>
            {renderRecommendationsTable(
              safePayload.trade_recommendations,
              "platform",
              (rec) => {
                setSelectedRecommendation(rec);
                setRecommendationDetailsOpen(true);
              },
              isLoading,
            )}
          </div>
        </Card>

        {isAdmin && (
          <ManualApprovalPanel
            currentMode={dataTradingMode}
            onModeChange={(mode) => modeChangeMutation.mutate(mode)}
            modeChangePending={modeChangeMutation.isPending}
          />
        )}

        <Card
          className="overview-card trading-system-card"
          title="Open positions & manual override"
          subtitle="Take control at any time: partial exit, full exit, adjust stops, or override the AI."
        >
          {renderPositionsTable(
            safePayload.active_positions,
            handlePositionControl,
            processingPositions,
            (position) => {
              setSelectedPosition(position);
              setPositionControlOpen(true);
            },
            isLoading,
            isAdmin,
          )}
        </Card>

        <Card
          className="overview-card trading-system-card"
          title="Executed trades"
          subtitle="Monitor completed trades with outcomes, PnL, and post-trade notes."
        >
          {renderExecutedTradesPanel()}
        </Card>

        <Card
          className="overview-card trading-system-card"
          title="Trades Report"
          subtitle="Comprehensive performance analysis of executed trades. Fees deducted from Total PnL."
        >
          {renderTradesReport(tradesReport)}
        </Card>

        <Card
          className="overview-card trading-system-card"
          title="Risk summary"
          subtitle="Key indicators provided by the advanced risk manager."
        >
          {renderRiskSummary(payload.risk)}
        </Card>

        <Card
          className="overview-card trading-system-card"
          title="Portfolio Heat & Capital Management"
          subtitle="Current portfolio risk metrics and capital allocation status."
        >
          {isLoadingCapitalData ? (
            <div style={{ padding: "var(--space-lg)", textAlign: "center", color: "rgba(203, 213, 225, 0.7)" }}>
              Loading capital management data...
            </div>
          ) : (
            <div className="capital-management-grid">
              {portfolioState && (
                <>
                  <div className="capital-card">
                    <strong>Total Equity</strong>
                    <span>${(portfolioState.total_equity ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Available Capital</strong>
                    <span>${(portfolioState.available_capital ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Allocated Capital</strong>
                    <span>${(portfolioState.allocated_capital ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Open Positions</strong>
                    <span>
                      {portfolioState.open_positions_count} / {portfolioState.max_positions}
                    </span>
                  </div>
                </>
              )}
              {slotMetrics && (
                <div className="capital-card slot-card">
                  <strong>Slot allocation</strong>
                  <div className="slot-card__metrics">
                    <div className="slot-card__metric">
                      <span>Per slot</span>
                      <strong>${(slotMetrics.perSlot ?? 0).toFixed(2)}</strong>
                    </div>
                    <div className="slot-card__metric">
                      <span>Next slot share</span>
                      <strong>${(slotMetrics.seatShare ?? 0).toFixed(2)}</strong>
                    </div>
                    <div className="slot-card__metric">
                      <span>Slots used</span>
                      <strong>
                        {slotMetrics.seatsInUse}/{slotMetrics.seatsTotal}
                      </strong>
                    </div>
                  </div>
                  <p className="slot-card__message">{slotMetrics.message}</p>
                </div>
              )}
              {portfolioHeat && (
                <>
                  <div
                    className={`capital-card heat-card ${
                      portfolioHeat.risk_level === "critical"
                        ? "critical"
                        : portfolioHeat.risk_level === "high"
                        ? "high"
                        : portfolioHeat.risk_level === "medium"
                        ? "medium"
                        : "low"
                    }`}
                  >
                    <strong>Portfolio Heat</strong>
                    <span>
                      {(portfolioHeat.heat_percentage ?? 0).toFixed(2)}% ({portfolioHeat.risk_level.toUpperCase()})
                    </span>
                  </div>
                  <div className="capital-card">
                    <strong>Current Heat</strong>
                    <span>${(portfolioHeat.current_heat ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Max Heat</strong>
                    <span>${(portfolioHeat.max_heat ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Available Risk Capacity</strong>
                    <span>${(portfolioHeat.available_risk_capacity ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="capital-card">
                    <strong>Can Open New Position</strong>
                    <span style={{ color: portfolioHeat.can_open_new_position ? "var(--color-success)" : "var(--color-error)" }}>
                      {portfolioHeat.can_open_new_position ? "Yes" : "No"}
                    </span>
                  </div>
                  {portfolioHeat.warnings && portfolioHeat.warnings.length > 0 && (
                    <div className="capital-card" style={{ gridColumn: "1 / -1" }}>
                      <strong>Warnings</strong>
                      <div className="warnings-list">
                        {portfolioHeat.warnings.map((warning, idx) => (
                          <div key={idx} className="warning-item">
                            {warning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              {queueLength > 0 && (
                <div className="capital-card queue-card">
                  <strong>Queued Trades</strong>
                  <span>{queueLength}</span>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card
          className="overview-card trading-system-card"
          title="Recommendation Queue Status"
          subtitle="Active and Reserve pools for recommendation management."
        >
          {isLoadingQueueData ? (
            <div style={{ padding: "var(--space-lg)", textAlign: "center", color: "rgba(203, 213, 225, 0.7)" }}>
              Loading recommendation queue data...
            </div>
          ) : queueStatus ? (
            <div className="capital-management-grid">
              <div className="capital-card">
                <strong>Active Pool</strong>
                <span>
                  {queueStatus.active_pool_size} / {queueStatus.active_pool_max}
                </span>
              </div>
              <div className="capital-card">
                <strong>Reserve Pool</strong>
                <span>
                  {queueStatus.reserve_pool_size} / {queueStatus.reserve_pool_max}
                </span>
              </div>
              <div className="capital-card">
                <strong>Total Recommendations</strong>
                <span>{queueStatus.active_pool_size + queueStatus.reserve_pool_size}</span>
              </div>
              <div className="capital-card">
                <strong>Pool Status</strong>
                <span style={{ 
                  color: queueStatus.active_pool_size >= queueStatus.active_pool_max ? "var(--color-success)" : "var(--color-warning)" 
                }}>
                  {queueStatus.active_pool_size >= queueStatus.active_pool_max ? "Full" : "Available"}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ padding: "var(--space-lg)", textAlign: "center", color: "rgba(203, 213, 225, 0.7)" }}>
              No queue data available
            </div>
          )}
        </Card>

      </section>

      <CustomPairsModal
        isOpen={customPairsOpen}
        onClose={() => setCustomPairsOpen(false)}
        onUpdate={loadPayload}
      />
      <PositionControlModal
        isOpen={positionControlOpen}
        onClose={() => {
          setPositionControlOpen(false);
          setSelectedPosition(null);
        }}
        position={selectedPosition}
        onControl={(action, params) => {
          if (selectedPosition) {
            handlePositionControl(selectedPosition.id, action, params);
            setPositionControlOpen(false);
            setSelectedPosition(null);
          }
        }}
      />
      <RecommendationDetailsModal
        isOpen={recommendationDetailsOpen}
        onClose={() => {
          setRecommendationDetailsOpen(false);
          setSelectedRecommendation(null);
        }}
        recommendation={selectedRecommendation}
      />
    </div>
  );
}

